import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPaymentMethodApi,
  deletePaymentMethodApi,
  getPaymentMethodApi,
  getPaymentMethodsApi,
  updatePaymentMethodApi,
} from "../api/paymentMethodsApi";
import type {
  RestCreatePaymentMethod,
  RestUpdatePaymentMethod,
} from "@KassaroAB/shared/types/paymentMethods/paymentMethod";
import type { PaymentMethodId, StoreId } from "@KassaroAB/shared/types/ids";

export const paymentMethodsQueryKey = (storeId: StoreId | undefined) => [
  "stores",
  storeId,
  "paymentMethods",
];

export const useGetPaymentMethodsQuery = (storeId: StoreId | undefined) => {
  return useQuery({
    queryKey: paymentMethodsQueryKey(storeId),
    queryFn: () => getPaymentMethodsApi(storeId!),
    enabled: !!storeId,
  });
};

export const useGetPaymentMethodQuery = (
  storeId: StoreId | undefined,
  paymentMethodId: PaymentMethodId | undefined,
) => {
  return useQuery({
    queryKey: [...paymentMethodsQueryKey(storeId), paymentMethodId],
    queryFn: () => getPaymentMethodApi(storeId!, paymentMethodId!),
    enabled: !!storeId && !!paymentMethodId,
  });
};

export const useCreatePaymentMethodMutation = (storeId: StoreId | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethod: RestCreatePaymentMethod) =>
      createPaymentMethodApi(storeId!, paymentMethod),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodsQueryKey(storeId),
      });
    },
  });
};

export const useUpdatePaymentMethodMutation = (storeId: StoreId | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentMethodId,
      paymentMethod,
    }: {
      paymentMethodId: PaymentMethodId;
      paymentMethod: RestUpdatePaymentMethod;
    }) => updatePaymentMethodApi(storeId!, paymentMethodId, paymentMethod),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodsQueryKey(storeId),
      });
    },
  });
};

export const useDeletePaymentMethodMutation = (storeId: StoreId | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: PaymentMethodId) =>
      deletePaymentMethodApi(storeId!, paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodsQueryKey(storeId),
      });
    },
  });
};
